import useSWR, { Key, SWRConfiguration, SWRResponse } from 'swr';
import { SearchResultWithAllFragment } from '@amf/shared/types/graphql';
import { useRef } from 'react';
import { Fetcher } from 'swr/esm/types';

export enum SearchScope {
  ALL = 'amf-all',
  NEWS = 'amf-news',
  LEAGUES = 'amf-leagues',
  PLAYERS = 'amf-players',
  TEAMS = 'amf-teams',
  CLUBS = 'amf-clubs',
  GROUNDS = 'amf-grounds',
}

function getKey(query: string | null, scope: SearchScope | null) {
  if (!query || query.length === 0 || !scope) return null;
  const params = new URLSearchParams({ query });
  if (scope !== SearchScope.ALL) params.append('scope', scope);

  return [`/api/search?${params.toString()}`, query, scope];
}

export function useSWRAbort<Data = any, Error = any>(
  key: Key,
  fn?: Fetcher<Data>,
  options?: SWRConfiguration<Data, Error>,
): SWRResponse<Data, Error> & {
  abort: () => void;
} {
  const aborter = useRef<AbortController>();
  const abort = () => aborter.current?.abort();

  const res = useSWR<Data, Error>(
    key,
    (...args) => {
      aborter.current = new AbortController();
      return fn?.(aborter.current.signal, ...args);
    },
    options,
  );

  return { ...res, abort };
}

function useAdvancedSearch(query: string | null = null, scope = SearchScope.ALL) {
  return useSWRAbort<SearchResultWithAllFragment[] | null>(
    getKey(query, scope),
    (signal, url) => {
      return fetch(url, { signal }).then(res => res.json());
    },
    {
      initialData: null,
      refreshWhenHidden: false,
      refreshWhenOffline: false,
      revalidateOnFocus: false,
      revalidateOnMount: false,
      revalidateOnReconnect: false,
    },
  );
}

export default useAdvancedSearch;
